/**
 * Admin API client — capstone grading queue + AI-grade trigger.
 *
 * All endpoints require an authenticated session with users.role='admin'.
 * Unauthenticated / non-admin callers get 401 / 403; the page handles
 * those by redirecting to login or rendering an "admin-only" notice.
 */
import { apiGet, apiPost } from "@/lib/api";

export interface CapstoneQueueRow {
  submission_id: string;
  user_id: string;
  user_display_name: string;
  user_email: string;
  enrollment_id: string;
  attempt_number: number;
  word_count: number;
  status: "draft" | "submitted" | "graded";
  submitted_at: string | null;
  grade_id: string | null;
  grade_composite: string | null;
  grade_pass_fail: "pass" | "fail" | null;
  grade_confidence_band: "high" | "borderline" | "low" | null;
  graded_at: string | null;
}

export interface GraderCriterionScore {
  id: string;
  score: number;
  rationale: string;
}

export interface GraderReasoning {
  model: string;
  confidence_band: "high" | "borderline" | "low";
  criteria: GraderCriterionScore[];
  overall_rationale: string;
}

export interface CapstoneGradeResult {
  grade_id: string;
  submission_id: string;
  composite_score: string;
  pass_fail: "pass" | "fail";
  rubric_scores: Record<string, number>;
  feedback: string;
  grader_reasoning: GraderReasoning;
  graded_at: string;
  cert_auto_issuance_eligible: boolean;
  issued_certificate_id: string | null;
  cert_issuance_error: string | null;
  requires_admin_review: boolean;
}

export type CapstoneStatusFilter = "submitted" | "graded" | "all";

export async function listAdminCapstones(
  filter: CapstoneStatusFilter = "submitted",
): Promise<CapstoneQueueRow[]> {
  const path = `/api/admin/capstones?status_filter=${encodeURIComponent(filter)}`;
  return apiGet<CapstoneQueueRow[]>(path);
}

export async function gradeCapstone(
  submissionId: string,
): Promise<CapstoneGradeResult> {
  const path = `/api/admin/capstones/${encodeURIComponent(submissionId)}/grade`;
  return apiPost<CapstoneGradeResult>(path);
}

// ---------------------------------------------------------------------------
// Learner dashboard
// ---------------------------------------------------------------------------

export interface LearnerListRow {
  user_id: string;
  name: string | null;
  email: string;
  role: string;
  enrollment_status: string;
  signed_in: boolean;
  lessons_complete: number;
  lessons_total: number;
  modules_passed: number;
  modules_total: number;
  exam_status: string;
  capstone_status: string;
  cert_issued: boolean;
  last_activity_at: string | null;
  at_risk_reasons: string[];
  /**
   * Gamification (GAM-US-9). Server-side these always come through with a
   * default of 0; declared optional here so legacy fixtures and pre-v0013
   * test rows continue to type-check. Read-sites use ``?? 0``.
   */
  badge_count?: number;
  total_xp?: number;
}

export interface LearnerListResponse {
  generated_at: string;
  total: number;
  at_risk_count: number;
  learners: LearnerListRow[];
}

export interface ModuleDetailRow {
  sequence: number;
  slug: string;
  title: string;
  lessons_complete: number;
  lessons_total: number;
  quiz_passed: boolean;
  quiz_attempts: number;
}

export interface ExamAttemptRow {
  attempt_number: number;
  status: string;
  score: number | null;
  started_at: string | null;
  submitted_at: string | null;
}

export interface LearnerCapstoneRow {
  attempt_number: number;
  status: string;
  word_count: number;
  submitted_at: string | null;
  updated_at: string | null;
  composite_score: number | null;
  pass_fail: string | null;
  graded_at: string | null;
}

export interface LearnerCertificateRow {
  issued: boolean;
  certificate_id: string | null;
  issued_at: string | null;
  status: string | null;
}

export interface LearnerDetailResponse {
  learner: LearnerListRow;
  modules: ModuleDetailRow[];
  exam_attempts: ExamAttemptRow[];
  capstones: LearnerCapstoneRow[];
  certificate: LearnerCertificateRow;
}

export async function listLearners(opts?: {
  archived?: boolean;
}): Promise<LearnerListResponse> {
  const qs = opts?.archived ? "?archived=true" : "";
  return apiGet<LearnerListResponse>(`/api/admin/learners${qs}`);
}

export async function getLearnerDetail(
  userId: string,
): Promise<LearnerDetailResponse> {
  return apiGet<LearnerDetailResponse>(
    `/api/admin/learners/${encodeURIComponent(userId)}`,
  );
}

// ---------------------------------------------------------------------------
// Archive / restore (v0019, DEC-0042)
// ---------------------------------------------------------------------------

export interface ArchiveActionResponse {
  user_id: string;
  archived_at: string | null;
}

export async function archiveLearner(
  userId: string,
): Promise<ArchiveActionResponse> {
  return apiPost<ArchiveActionResponse>(
    `/api/admin/learners/${encodeURIComponent(userId)}/archive`,
  );
}

export async function restoreLearner(
  userId: string,
): Promise<ArchiveActionResponse> {
  return apiPost<ArchiveActionResponse>(
    `/api/admin/learners/${encodeURIComponent(userId)}/restore`,
  );
}

export interface DeleteActionResponse {
  user_id: string;
  deleted_at: string;
}

/**
 * Permanently hard-delete an archived, never-signed-in learner. Irreversible.
 * 400 if not archived first / target is the calling admin / target is an admin.
 * 409 if the learner has ever signed in (workos_user_id set).
 */
export async function deleteLearner(
  userId: string,
): Promise<DeleteActionResponse> {
  return apiPost<DeleteActionResponse>(
    `/api/admin/learners/${encodeURIComponent(userId)}/delete`,
  );
}

// ---------------------------------------------------------------------------
// Create learner (reuses the existing enrollment endpoints)
// ---------------------------------------------------------------------------

export interface SeedDataCourseVersion {
  id: string;
  course_id: string;
  course_slug: string;
  course_title: string;
  version: string;
}

export interface SeedDataPriceTier {
  id: string;
  label: string;
  stripe_price_id: string;
  inventory_total: number | null;
  inventory_used: number;
}

export interface AdminSeedData {
  active_course_versions: SeedDataCourseVersion[];
  active_price_tiers: SeedDataPriceTier[];
}

export interface CreateEnrollmentBody {
  email: string;
  course_version_id: string;
  price_tier_id: string;
  name?: string;
}

export interface CreateEnrollmentResult {
  enrollment_id: string;
  user_id: string;
  status: string;
  email_queued: boolean;
  message: string;
}

export async function getAdminSeedData(): Promise<AdminSeedData> {
  return apiGet<AdminSeedData>("/api/admin/seed-data");
}

export async function createEnrollment(
  body: CreateEnrollmentBody,
): Promise<CreateEnrollmentResult> {
  return apiPost<CreateEnrollmentResult>("/api/admin/enrollments", body);
}
